import type { Command, ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { ContentNodeWithPos } from '@atlaskit/editor-prosemirror/utils';
import {
	EDITOR_TOOLBAR_HANDLER_KEYS,
	type AlignmentValue,
	type EditorToolbarHandlerContext,
	type EditorToolbarHandlers,
	type NativeEmbedAppearance,
	type NativeEmbedParameterValues,
} from '@atlaskit/native-embeds-common';

import type {
	EditorPluginNativeEmbedsPlugin,
	EditorToolbarHandlersWithDefaults,
} from '../../nativeEmbedsPluginType';
import {
	createCopyLinkCommand,
	createDeleteCommand,
	createOpenInNewWindowCommand,
	createToggleAlwaysShowTitle,
	createUpdateAlignmentCommand,
} from '../commands';
import { setNativeEmbedAppearance } from '../commands/appearance';
import { showUrlToolbar } from '../commands/url';

const createHandlerCommand =
	(handler?: () => void): Command =>
	() => {
		handler?.();
		return true;
	};

function wrapManifestHandler(
	handler: (p: NativeEmbedParameterValues, context?: EditorToolbarHandlerContext) => void,
	parameters: NativeEmbedParameterValues,
	handlerContext: EditorToolbarHandlerContext,
): () => Command {
	return () => createHandlerCommand(() => handler(parameters, handlerContext));
}

function wrapManifestHandlerWithContext<C>(
	handler: (
		p: NativeEmbedParameterValues,
		context: C,
		handlerContext?: EditorToolbarHandlerContext,
	) => void,
	parameters: NativeEmbedParameterValues,
	handlerContext: EditorToolbarHandlerContext,
): (context: C) => Command {
	return (context) => createHandlerCommand(() => handler(parameters, context, handlerContext));
}

/**
 * Creates default EditorToolbarHandlers with actual functionality.
 * Injects editorAPI for commands that need it (e.g. alignment, open-in-new-window).
 */
export function createDefaultEditorToolbarHandlers(
	selectedNativeEmbed: ContentNodeWithPos,
	api: ExtractInjectionAPI<EditorPluginNativeEmbedsPlugin> | undefined,
): EditorToolbarHandlersWithDefaults {
	const node = selectedNativeEmbed.node;

	return {
		onAppearanceClick: (appearance: NativeEmbedAppearance) =>
			setNativeEmbedAppearance(selectedNativeEmbed, appearance, api?.analytics?.actions),
		onAlignmentClick: (alignment: AlignmentValue) =>
			createUpdateAlignmentCommand(api, alignment, node),
		onCopyLinkClick: () => createCopyLinkCommand(node),
		onDeleteClick: () => createDeleteCommand(selectedNativeEmbed),
		onShowTitleClick: () => createHandlerCommand(createToggleAlwaysShowTitle(api, node)),
		onOpenInNewWindowClick: () => createOpenInNewWindowCommand(node),
		onEditUrlClick: () => showUrlToolbar,
		onEditClick: () => showUrlToolbar,
		onAskRovoClick: () => createHandlerCommand(() => {}),
		onRefreshClick: () => createHandlerCommand(() => {}),
		onChangeBorderClick: () => createHandlerCommand(() => {}),
		onSetEmbedTypeClick: () => createHandlerCommand(() => {}),
	};
}

/**
 * Builds an EditorToolbarHandlersWithDefaults object by merging manifest handlers with default handlers.
 * For each handler key, uses the manifest handler if defined, otherwise the default action.
 */
export function buildToolbarHandlers(
	selectedNativeEmbed: ContentNodeWithPos,
	manifestHandlers: EditorToolbarHandlers | undefined,
	api: ExtractInjectionAPI<EditorPluginNativeEmbedsPlugin> | undefined,
	parameters: NativeEmbedParameterValues,
): EditorToolbarHandlersWithDefaults {
	const defaultHandlers = createDefaultEditorToolbarHandlers(selectedNativeEmbed, api);
	const result: EditorToolbarHandlersWithDefaults = { ...defaultHandlers };
	const handlerContext: EditorToolbarHandlerContext = {
		editorApi: api,
	};

	for (const key of EDITOR_TOOLBAR_HANDLER_KEYS) {
		const manifestHandler = manifestHandlers?.[key];
		if (!manifestHandler) continue;

		switch (key) {
			case 'onAlignmentClick':
				result.onAlignmentClick = wrapManifestHandlerWithContext<AlignmentValue>(
					manifestHandler as NonNullable<EditorToolbarHandlers['onAlignmentClick']>,
					parameters,
					handlerContext,
				);
				break;
			case 'onAppearanceClick':
				result.onAppearanceClick = wrapManifestHandlerWithContext<NativeEmbedAppearance>(
					manifestHandler as NonNullable<EditorToolbarHandlers['onAppearanceClick']>,
					parameters,
					handlerContext,
				);
				break;
			default:
				result[key] = wrapManifestHandler(
					manifestHandler as (
						p: NativeEmbedParameterValues,
						context?: EditorToolbarHandlerContext,
					) => void,
					parameters,
					handlerContext,
				);
		}
	}

	return result;
}
